import { useParams } from 'react-router-dom';
import { useBaseParam } from '../hooks/useBaseParam';
import { useEffect, useState } from 'react';
import { putLoanChange } from '../../infra/api';
import { useBridge } from '../hooks/useBridge';
import useNav from '../hooks/useNav';
import useBeacon from '../hooks/useBeacon';
import { PAGE_NM } from '../types/types';
import { useRecoilValue } from 'recoil';
import { beaconSelector } from '../recoil/selector';
import { fetchTotalProductDetail } from '../../infra/api/fetchTotalProductDetail';
import useToast from '../hooks/useToast';

const useProdDetail = () => {
  const { historyBack } = useNav();
  const { requestSeq } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [detailInfo, setDetailInfo] = useState<any>({});
  const { addToast } = useToast();
  const [popup, setPopup] = useState({
    message: `취소한 대출은 하루 동안 신청할 수 없어요.\n 그래도 취소하시겠어요?`,
    open: false,
    cancelText: '예',
    handleClickCancel: () => {},
    confirmText: '아니요',
    handleClickConfirm: () => setPopup((prev) => ({ ...prev, open: false })),
  });
  const br: any = useBridge();
  const { productInfo, custRequestProductInfo, custPreProductInfo } =
    detailInfo;

  const withHistoryBackOnSuccess = (fn: () => Promise<boolean>) => {
    return async () => {
      const res = await fn();
      if (res) historyBack();
    };
  };
  const getBtnText = (status: string) => {
    return status === '01'
      ? '대출 신청 중(자세히 보기)'
      : status === '02'
      ? '대출 심사 중(자세히 보기)'
      : status === '03'
      ? '대출 완료(자세히 보기)'
      : '';
  };
  const getIsLoanComplete = (status: string, loanCancelYn: string) => {
    return status === '03' || loanCancelYn === 'N';
  };
  const getIsCanceledItem = (status: string) => {
    return status === '99';
  };
  const navigateLink = (linkTypeCd: string, link: string) => {
    if (linkTypeCd === 'OUT') {
      br.openExternalBrowser({
        url: link,
      });
    } else if (linkTypeCd === 'IN') {
      br.openNativeWebView({
        url: link,
        title: '상세보기',
        closeType: 'B',
      });
    }
  };

  const getTotalProductDetail = useBaseParam(fetchTotalProductDetail, {
    requestSeq: Number(requestSeq),
  });
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const res = await getTotalProductDetail();
      if (res?.data?.isSucceed === true) {
        setDetailInfo(res.data.result);
      }
      setIsLoading(false);
    })();
  }, [requestSeq]);

  const mutateLoanChange = useBaseParam(putLoanChange, {
    requestSeq: Number(requestSeq),
  });
  const mutateCancelLoan = async () => {
    const result: any = await mutateLoanChange();
    if (result?.data?.isSucceed === true) {
      addToast('대출 취소 신청되었습니다.');
      return true;
    } else {
      return false;
    }
  };

  const cancelLoan = () => {
    if (getIsCanceledItem(custRequestProductInfo?.status)) {
      addToast('금융사 페이지에서 신청하지 않아 자동 취소되었습니다.');
    }

    setPopup((prev) => ({
      ...prev,
      open: true,
      handleClickCancel: async () => {
        const cancelFn = withHistoryBackOnSuccess(mutateCancelLoan);
        await cancelFn();
        setPopup((prev) => ({ ...prev, open: false }));
      },
    }));
  };

  const navigateExternal = () => {
    if (getIsCanceledItem(custRequestProductInfo?.status))
      addToast('금융사 페이지에서 신청하지 않아 자동 취소되었습니다.');
    sendBeaconLandingProduct(productInfo?.detailLink);
    navigateLink(productInfo?.detailLinkTypeCd, productInfo?.detailLink);
  };

  //  beacon
  const beaconData = useRecoilValue(beaconSelector);
  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: PAGE_NM.DETAIL,
  });
  useEffect(() => {
    beacon.sendViewPage();
  }, []);
  const sendBeaconLoanApp = (btnNm: string) => {
    beacon.sendClkBtnLoanApp({
      properties: { btnNm },
    });
  };
  const sendBeaconLoanCncl = (btnNm: string) => {
    beacon.sendClkBtnLoanCncl({
      properties: { btnNm },
    });
  };

  const sendBeaconLandingProduct = (url: string) => {
    const productSeq = productInfo?.productSeq;
    const preRequestDtlNo = custPreProductInfo?.preRequestDtlNo;
    beacon.sendLandingProduct({
      properties: { productSeq, preRequestDtlNo, url },
    });
  };

  const sendBeaconClkBtnIntrstCalc = (btnNm: string) => {
    beacon.sendClkBtnIntrstCalc({
      properties: { btnNm },
    });
  };

  return {
    isLoading,
    custProductDetailItem: custRequestProductInfo,
    custPreProductInfo,
    productInfo,
    productOptions: productInfo?.productOptions?.map((item: any) => ({
      title: item.titleTypeCdNm,
      description: item.description,
      key: item.titleTypeCd,
    })),
    hashTagList: productInfo?.hashTagList?.map((item: any) => ({
      color: item.color,
      backgroundColor: item.backgroundColor,
      text: item.tagNm,
    })),
    isLoanComplete: getIsLoanComplete(
      custRequestProductInfo?.status,
      productInfo?.loanCancelYn,
    ),
    btnText: getBtnText(custRequestProductInfo?.status),
    isCanceledItem: getIsCanceledItem(custRequestProductInfo?.status), // 추후 금융사별 코드이므로 금융사 조건 필요
    cancelLoan,
    navigateExternal,
    popup,
    sendBeaconLoanApp,
    sendBeaconLoanCncl,
    sendBeaconClkBtnIntrstCalc,
  };
};

export default useProdDetail;
